import Papa from 'papaparse';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { parseJobDate } from './dateUtils';

/**
 * Export jobs data to CSV format
 * @param {Array} jobs - Array of job objects
 * @param {string} companyName - Company name for filename
 */
export const exportToCSV = (jobs, companyName = 'Company') => {
    // Prepare data for CSV
    const csvData = jobs.map(job => ({
        'Skip ID': job.skipId || '',
        'Address': job.formattedAddress || job.streetAddress || '',
        'Date': (() => { const d = parseJobDate(job.initialTime, job.timestamp); return d ? d.toLocaleDateString('en-GB') : ''; })(),
        'Time': (() => { const d = parseJobDate(job.initialTime, job.timestamp); return d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''; })(),
        'Size': job.size || '',
        'Waste Type': job.wasteType || job.description || '',
        'Job Type': job.jobType || job.name || '',
        'Status': job.status || '',
        'Driver': job.driverName || '',
        'Invoiced': job.invoiced ? 'Yes' : 'No'
    }));

    // Convert to CSV using PapaParse
    const csv = Papa.unparse(csvData);

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `${companyName.replace(/\s/g, '_')}_skip_records_${timestamp}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Export jobs data to PDF format with enhanced print optimization
 * @param {Array} jobs - Array of job objects
 * @param {string} companyName - Company name for report header
 */
export const exportToPDF = (jobs, companyName = 'Company') => {
    try {
        const doc = new jsPDF('landscape', 'mm', 'a4'); // Landscape for better table fit

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Document generation timestamp
        const generatedDate = new Date().toLocaleDateString('en-GB');
        const generatedTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

        // Prepare table data with better formatting
        const tableData = jobs.map(job => [
            (job.skipId || '').substring(0, 8),
            job.formattedAddress || job.streetAddress || 'N/A',
            (() => { const d = parseJobDate(job.initialTime, job.timestamp); return d ? d.toLocaleDateString('en-GB') : 'N/A'; })(),
            (() => { const d = parseJobDate(job.initialTime, job.timestamp); return d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'N/A'; })(),
            job.size || 'N/A',
            job.wasteType || job.description || 'N/A',
            job.jobType || job.name || 'N/A',
            job.driverName || 'Unassigned',
            (job.status || 'N/A').toUpperCase(),
            job.invoiced ? 'Yes' : 'No'
        ]);

        // Add table using autoTable with enhanced styling
        autoTable(doc, {
            head: [['Ref', 'Delivery Address', 'Date', 'Time', 'Size', 'Waste Type', 'Job Type', 'Driver', 'Status', 'Inv']],
            body: tableData,
            startY: 25,
            margin: { left: 10, right: 10, top: 20, bottom: 20 },

            // Enhanced styles for better print quality
            styles: {
                fontSize: 9,
                cellPadding: 3,
                lineColor: [0, 0, 0],
                lineWidth: 0.25,
                textColor: [0, 0, 0],
                overflow: 'linebreak',
                halign: 'left'
            },

            // Header styling
            headStyles: {
                fillColor: [59, 130, 246],
                textColor: [255, 255, 255],
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center',
                lineWidth: 0.5
            },

            // Alternating row colors for better readability
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },

            // Column-specific styling
            columnStyles: {
                0: { cellWidth: 18, halign: 'left', fontStyle: 'normal', fontSize: 8 }, // Ref
                1: { cellWidth: 58, halign: 'left', fontStyle: 'bold' }, // Address (most prominent)
                2: { cellWidth: 22, halign: 'center' }, // Date
                3: { cellWidth: 18, halign: 'center' }, // Time
                4: { cellWidth: 16, halign: 'center', fontStyle: 'bold' }, // Size (bold for emphasis)
                5: { cellWidth: 24, halign: 'left' }, // Waste Type
                6: { cellWidth: 20, halign: 'left' }, // Job Type
                7: { cellWidth: 26, halign: 'left' }, // Driver
                8: { cellWidth: 20, halign: 'center', fontStyle: 'bold' }, // Status (bold)
                9: { cellWidth: 14, halign: 'center' } // Inv
            },

            // Add header and footer to each page
            didDrawPage: (data) => {
                // Header on each page
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(`${companyName.toUpperCase()} - SKIP RECORDS REPORT`, pageWidth / 2, 15, { align: 'center' });

                // Report info
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(`Generated: ${generatedDate} ${generatedTime}`, 10, 20);
                doc.text(`Total Records: ${jobs.length}`, pageWidth - 10, 20, { align: 'right' });

                // Footer on each page
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');

                // Page number (center)
                doc.text(
                    `Page ${currentPage} of ${totalPages}`,
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );

                // Company name in footer (left)
                doc.setFontSize(8);
                doc.text(companyName, 10, pageHeight - 10);

                // Timestamp in footer (right)
                doc.text(`Printed: ${generatedDate}`, pageWidth - 10, pageHeight - 10, { align: 'right' });

                // Border around page (optional - for professional look)
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.5);
                doc.rect(5, 5, pageWidth - 10, pageHeight - 10);
            }
        });

        // Download the PDF with timestamp in filename
        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`${companyName.replace(/\s/g, '_')}_skip_records_${timestamp}.pdf`);
    } catch (error) {
        console.error('PDF Export Error:', error);
        throw error;
    }
};

/**
 * Export activity log events to CSV format
 * @param {Array} events - Array of event objects
 * @param {string} companyName - Company name for filename
 */
export const exportLogToCSV = (events, companyName = 'Company') => {
    // Prepare data for CSV
    const csvData = events.map(event => ({
        'Timestamp': event.timestamp.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }),
        'Action': event.type === 'delivery' ? 'Delivery' : 'Collection',
        'Driver': event.driver || '',
        'Address': event.address || '',
        'Size': event.size || '',
        'Waste Type': event.wasteType || '',
        'Payment': event.paymentStatus || '',
        'Job ID': event.jobId || '',
        'Exchange': event.linkedJobId ? 'Yes' : 'No'
    }));

    // Convert to CSV using PapaParse
    const csv = Papa.unparse(csvData);

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `${companyName.replace(/\s/g, '_')}_activity_log_${timestamp}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Export activity log events to PDF format
 * @param {Array} events - Array of event objects
 * @param {string} companyName - Company name for report header
 */
export const exportLogToPDF = (events, companyName = 'Company') => {
    try {
        const doc = new jsPDF('landscape', 'mm', 'a4');

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const generatedDate = new Date().toLocaleDateString('en-GB');
        const generatedTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

        // Prepare table data
        const tableData = events.map(event => [
            event.timestamp.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }),
            event.type === 'delivery' ? 'Delivery' : 'Collection',
            event.driver || 'Unknown',
            event.address || 'N/A',
            event.size || 'N/A',
            event.wasteType || 'N/A',
            event.paymentStatus || 'Unknown',
            event.jobId || 'N/A',
            event.linkedJobId ? '🔗' : ''
        ]);

        // Add table
        autoTable(doc, {
            head: [['Timestamp', 'Action', 'Driver', 'Address', 'Size', 'Waste', 'Payment', 'Ref', 'Exch']],
            body: tableData,
            startY: 25,
            margin: { left: 10, right: 10, top: 20, bottom: 20 },

            styles: {
                fontSize: 8,
                cellPadding: 2.5,
                lineColor: [0, 0, 0],
                lineWidth: 0.25,
                textColor: [0, 0, 0],
                overflow: 'linebreak',
                halign: 'left'
            },

            headStyles: {
                fillColor: [59, 130, 246],
                textColor: [255, 255, 255],
                fontSize: 9,
                fontStyle: 'bold',
                halign: 'center',
                lineWidth: 0.5
            },

            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },

            columnStyles: {
                0: { cellWidth: 35, halign: 'left' }, // Timestamp
                1: { cellWidth: 22, halign: 'center', fontStyle: 'bold' }, // Action
                2: { cellWidth: 25, halign: 'left' }, // Driver
                3: { cellWidth: 55, halign: 'left', fontStyle: 'bold' }, // Address
                4: { cellWidth: 14, halign: 'center' }, // Size
                5: { cellWidth: 20, halign: 'left' }, // Waste
                6: { cellWidth: 18, halign: 'center' }, // Payment
                7: { cellWidth: 28, halign: 'left', fontSize: 7 }, // Ref (Job ID - increased for full skipId)
                8: { cellWidth: 10, halign: 'center' } // Exchange
            },

            didDrawPage: (data) => {
                // Header
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text(`${companyName.toUpperCase()} - ACTIVITY LOG`, pageWidth / 2, 15, { align: 'center' });

                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.text(`Generated: ${generatedDate} ${generatedTime}`, 10, 20);
                doc.text(`Total Events: ${events.length}`, pageWidth - 10, 20, { align: 'right' });

                // Footer
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();

                doc.setFontSize(9);
                doc.text(
                    `Page ${currentPage} of ${totalPages}`,
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );

                doc.setFontSize(8);
                doc.text(companyName, 10, pageHeight - 10);
                doc.text(`Printed: ${generatedDate}`, pageWidth - 10, pageHeight - 10, { align: 'right' });

                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.5);
                doc.rect(5, 5, pageWidth - 10, pageHeight - 10);
            }
        });

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`${companyName.replace(/\s/g, '_')}_activity_log_${timestamp}.pdf`);
    } catch (error) {
        console.error('PDF Export Error:', error);
        throw error;
    }
};
